/**
 * Target Environment Detection Utility
 *
 * This module provides unified URL-based target environment detection
 * for both server and client side. The target is detected from the hostname
 * and persisted in cookies for consistent access across the application.
 *
 * Detection Priority:
 * 1. Cookie (if already set)
 * 2. NEXT_PUBLIC_LOCAL_TARGET_ENV (for local development override)
 * 3. URL hostname detection
 * 4. URL query parameter (?target=xxx)
 * 5. Default to samwise for localhost, staging otherwise
 */

import { WebAppEnvironment } from '@/types/api';

/**
 * Default target environment for local development.
 * This can be overridden by setting NEXT_PUBLIC_LOCAL_TARGET_ENV in .env
 */
export const DEFAULT_LOCAL_TARGET = WebAppEnvironment.SAMWISE;

/**
 * URL patterns for environment detection
 * Order matters - more specific patterns should come first
 */
const HOSTNAME_PATTERNS: Array<{
  pattern: RegExp | string;
  environment: WebAppEnvironment;
}> = [
  // Production domains
  { pattern: 'editor.how.fm', environment: WebAppEnvironment.PRODUCTION },
  { pattern: 'my.how.fm', environment: WebAppEnvironment.PRODUCTION },
  { pattern: 'appeu.how.fm', environment: WebAppEnvironment.PRODUCTION },

  // Dev environments - check subdomain patterns
  { pattern: /\.samwise\.howfm\.io$/, environment: WebAppEnvironment.SAMWISE },
  { pattern: /\.frodo\.howfm\.io$/, environment: WebAppEnvironment.FRODO },
  { pattern: /\.merry\.howfm\.io$/, environment: WebAppEnvironment.MERRY },
  { pattern: /\.pippin\.howfm\.io$/, environment: WebAppEnvironment.PIPPIN },
  { pattern: /\.bilbo\.howfm\.io$/, environment: WebAppEnvironment.BILBO },

  // Also match without leading dot for exact subdomain matches
  { pattern: /samwise\.howfm\.io/, environment: WebAppEnvironment.SAMWISE },
  { pattern: /frodo\.howfm\.io/, environment: WebAppEnvironment.FRODO },
  { pattern: /merry\.howfm\.io/, environment: WebAppEnvironment.MERRY },
  { pattern: /pippin\.howfm\.io/, environment: WebAppEnvironment.PIPPIN },
  { pattern: /bilbo\.howfm\.io/, environment: WebAppEnvironment.BILBO },

  // Staging patterns
  { pattern: /staging\.howfm\.io/, environment: WebAppEnvironment.STAGING },
  { pattern: /develop\.howfm\.io/, environment: WebAppEnvironment.STAGING },
  { pattern: /poc-staging/, environment: WebAppEnvironment.STAGING },
];

/**
 * Detects target environment from hostname
 *
 * @param hostname - The hostname to analyze (e.g., 'editor.samwise.howfm.io')
 * @returns The detected WebAppEnvironment or null if no match
 */
export function detectTargetFromHostname(hostname: string): WebAppEnvironment | null {
  if (hostname === '' || hostname === null || hostname === undefined) {
    return null;
  }

  const normalizedHostname = hostname.toLowerCase();

  // Check exact matches and patterns
  for (const { pattern, environment } of HOSTNAME_PATTERNS) {
    if (typeof pattern === 'string') {
      if (normalizedHostname === pattern) {
        return environment;
      }
    } else if (pattern.test(normalizedHostname)) {
      return environment;
    }
  }

  // Local development - check for env override first, then use default (samwise)
  if (normalizedHostname === 'localhost' || normalizedHostname === '127.0.0.1') {
    // Check for local target override from environment variable
    const localTargetOverride = process.env.NEXT_PUBLIC_LOCAL_TARGET_ENV;
    if (localTargetOverride !== undefined && localTargetOverride !== '') {
      const validEnvironments = Object.values(WebAppEnvironment);
      const normalizedOverride = localTargetOverride.toLowerCase();
      const matchedEnv = validEnvironments.find(
        env => env.toLowerCase() === normalizedOverride,
      );
      if (matchedEnv !== undefined) {
        return matchedEnv;
      }
    }
    // Default to samwise for local development
    return DEFAULT_LOCAL_TARGET;
  }

  return null;
}

/**
 * Detects target environment from URL query parameter
 *
 * @param searchParams - URL search params string or URLSearchParams object
 * @returns The detected WebAppEnvironment or null if no valid target param
 */
export function detectTargetFromQueryParam(
  searchParams: string | URLSearchParams | null | undefined,
): WebAppEnvironment | null {
  if (searchParams === null || searchParams === undefined) {
    return null;
  }

  const params = typeof searchParams === 'string'
    ? new URLSearchParams(searchParams)
    : searchParams;

  const targetParam = params.get('target');

  if (targetParam === null || targetParam === '') {
    return null;
  }

  const validEnvironments = Object.values(WebAppEnvironment);
  const normalizedTarget = targetParam.toLowerCase();
  const matchedEnv = validEnvironments.find(
    env => env.toLowerCase() === normalizedTarget,
  );

  return matchedEnv !== undefined ? matchedEnv : null;
}

/**
 * Server-side target detection from request headers
 *
 * Extracts hostname from Next.js headers and detects target environment.
 * This should be called in server components or API routes.
 *
 * @returns The detected WebAppEnvironment or samwise as default for localhost
 */
export async function detectTargetFromHeaders(): Promise<WebAppEnvironment> {
  try {
    const { headers } = await import('next/headers');
    const headersList = await headers();

    // Get hostname from x-forwarded-host or host header
    const forwardedHost = headersList.get('x-forwarded-host');
    const host = headersList.get('host');
    const hostname = forwardedHost ?? host ?? '';

    // Remove port if present
    const cleanHostname = hostname.split(':')[0] ?? '';

    const detected = detectTargetFromHostname(cleanHostname);
    return detected ?? WebAppEnvironment.STAGING;
  } catch {
    // Fallback to staging if headers are not available
    return WebAppEnvironment.STAGING;
  }
}

/**
 * Client-side target detection from window.location
 *
 * @returns The detected WebAppEnvironment or null
 */
export function detectTargetFromWindow(): WebAppEnvironment | null {
  if (typeof window === 'undefined') {
    return null;
  }

  // First check query parameter
  const fromQuery = detectTargetFromQueryParam(window.location.search);
  if (fromQuery !== null) {
    return fromQuery;
  }

  // Then check hostname
  return detectTargetFromHostname(window.location.hostname);
}

/**
 * Validates if a string is a valid WebAppEnvironment
 *
 * @param value - The value to validate
 * @returns True if the value is a valid WebAppEnvironment
 */
export function isValidTargetEnvironment(value: string | null | undefined): value is WebAppEnvironment {
  if (value === null || value === undefined || value === '') {
    return false;
  }

  return Object.values(WebAppEnvironment).includes(value as WebAppEnvironment);
}

// =============================================================================
// UNIFIED TARGET ENVIRONMENT GETTERS - Single Source of Truth
// =============================================================================

/**
 * Server-side unified target environment getter.
 *
 * This is the SINGLE SOURCE OF TRUTH for getting target environment on server.
 *
 * Detection priority:
 * 1. Cookie (targetEnv)
 * 2. URL hostname detection from headers
 * 3. Default (samwise for localhost, staging otherwise)
 *
 * @returns Promise<WebAppEnvironment> The resolved target environment
 */
export async function getTargetEnvironmentServer(): Promise<WebAppEnvironment> {
  try {
    const { cookies, headers } = await import('next/headers');

    // 1. Check cookie first (primary source)
    const cookieStore = await cookies();
    const cookieValue = cookieStore.get('targetEnv')?.value;
    if (isValidTargetEnvironment(cookieValue)) {
      return cookieValue;
    }

    // 2. Detect from URL hostname (headers)
    const headersList = await headers();
    const forwardedHost = headersList.get('x-forwarded-host');
    const host = headersList.get('host');
    const hostname = forwardedHost ?? host ?? '';
    const cleanHostname = hostname.split(':')[0] ?? '';

    const detected = detectTargetFromHostname(cleanHostname);
    if (detected !== null) {
      return detected;
    }

    // 3. Default to samwise for local, staging otherwise
    if (cleanHostname === 'localhost' || cleanHostname === '127.0.0.1') {
      return DEFAULT_LOCAL_TARGET;
    }

    return WebAppEnvironment.STAGING;
  } catch {
    // Fallback if headers/cookies not available
    return DEFAULT_LOCAL_TARGET;
  }
}

/**
 * Client-side unified target environment getter.
 *
 * This is the SINGLE SOURCE OF TRUTH for getting target environment on client.
 *
 * Detection priority:
 * 1. Cookie (targetEnv)
 * 2. URL query parameter (?target=xxx)
 * 3. URL hostname detection
 * 4. Default (samwise for localhost, staging otherwise)
 *
 * @returns WebAppEnvironment The resolved target environment
 */
export function getTargetEnvironmentClient(): WebAppEnvironment {
  if (typeof window === 'undefined') {
    // Server-side fallback (shouldn't be used on server, but just in case)
    return DEFAULT_LOCAL_TARGET;
  }

  // 1. Check cookie first (primary source)
  // Use a simple cookie parser to avoid circular dependencies
  const cookieValue = document.cookie
    .split('; ')
    .find(row => row.startsWith('targetEnv='))
    ?.split('=')[1];

  if (isValidTargetEnvironment(cookieValue)) {
    return cookieValue;
  }

  // 2. Check URL query parameter
  const fromQuery = detectTargetFromQueryParam(window.location.search);
  if (fromQuery !== null) {
    return fromQuery;
  }

  // 3. Detect from hostname
  const fromHostname = detectTargetFromHostname(window.location.hostname);
  if (fromHostname !== null) {
    return fromHostname;
  }

  // 4. Default
  return DEFAULT_LOCAL_TARGET;
}
